import { Fragment, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { AlertDialog } from "@hamlak/ui";
import { logout } from "@/api/client";
import { api, call, xClient } from "@/api/spec";
import type { Notice } from "@/api/types";
import { platform } from "@/platform";
import { t } from "@/i18n/ko";
import { isStoreCode, storeKey } from "@/i18n/codes";
import { formatMoney } from "@/features/store/money";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";

/** 서버 RefundRevokedPayload(apps/server/src/me/notices.ts). 예전 안내엔 store·금액이 없을 수 있다 */
type RevokedPayload = { revoked?: number; balance?: number; store?: string; currency?: string; amountMicros?: number };

/** 시안 F06-14 본문: "{스토어}에서 환불된 결제({금액})가 있어서, … 보유 브릭을 {잔액}브릭으로 정리했어요." 잔액은 회수 직후 값(payload.balance) */
export function refundRevokedBody(payload: RevokedPayload, fallbackBalance: number): string {
  const price =
    typeof payload.currency === "string" && typeof payload.amountMicros === "number"
      ? t("notice.refundRevoked.price", { amount: formatMoney({ currency: payload.currency, amountMicros: payload.amountMicros }) })
      : "";
  const balance = (typeof payload.balance === "number" ? payload.balance : fallbackBalance).toLocaleString("ko-KR");
  return isStoreCode(payload.store)
    ? t("notice.refundRevoked.body", { store: t(storeKey("notice.refundRevoked.store", payload.store)), price, balance })
    : t("notice.refundRevoked.bodyNoStore", { price, balance });
}

/** 문구의 \n 을 줄바꿈으로(AlertDialog description 은 <p> 한 개). */
export function paragraphs(text: string) {
  return text.split("\n").map((line, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {line}
    </Fragment>
  ));
}

/**
 * F06-15 [확인]: 로그아웃(서버 세션 폐기 시도 + 네이티브 signOut + 쿼리 캐시 비움) → /login. NoticeDialogs·/blocked 공용.
 * `before` 는 로그아웃 전에 할 일(안내 ack 등).
 */
export function useBlockedConfirm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const confirm = async (before?: () => Promise<void>) => {
    setBusy(true);
    try {
      await before?.();
      await logout();
      await platform.auth.signOut().catch(() => undefined);
      qc.clear();
    } finally {
      setBusy(false);
      navigate("/login", { replace: true });
    }
  };
  return { busy, confirm };
}

/**
 * 1.22 NoticeDialogs(F06-14 회수 안내 · F06-15 이용 제한). GET /me.notices 중 shownNotices에 없는 첫 항목을 1회 띄우고
 * POST /me/notices/ack {id}(실패 무시, 멱등) + 로컬 기록. 두 안내 모두 [고객센터 문의] → /support. blocked: [확인] → 로그아웃 → /login.
 */
export function NoticeDialogs() {
  const navigate = useNavigate();
  const notices = useAuthStore((s) => s.notices);
  const balance = useAuthStore((s) => s.balance);
  const shown = useUiStore((s) => s.shownNotices);
  const markShown = useUiStore((s) => s.markNoticeShown);
  const { busy, confirm } = useBlockedConfirm();

  const current: Notice | undefined = notices.find((n) => !shown.includes(n.id));

  if (!current) return null;

  const ack = async () => {
    markShown(current.id);
    try {
      await call(api.POST("/me/notices/ack", { params: { header: xClient() }, body: { id: current.id } }));
    } catch {
      // 멱등. 다음 GET /me에서 다시 내려오면 그때 다시 ack.
    }
  };

  if (current.type === "blocked") {
    return (
      // 시안 F06-15(382:1882): neutral pair — 왼쪽 [확인](보조) · 오른쪽 [고객센터 문의](주요), 본문 두 문단
      <AlertDialog
        open
        action="pair"
        title={t("blocked.account.title")}
        description={paragraphs(t("notice.blocked.body"))}
        primaryLabel={t("blocked.account.support")}
        secondaryLabel={t("blocked.account.cta")}
        loading={busy}
        onPrimary={() => {
          void ack();
          navigate("/support");
        }}
        onSecondary={() => void confirm(ack)}
        onDismiss={() => undefined}
      />
    );
  }

  const description = refundRevokedBody((current.payload ?? {}) as RevokedPayload, balance ?? 0);
  // 시안 F06-14(346:1865): pair — 왼쪽 [확인](보조) · 오른쪽 [고객센터 문의](주요)
  return (
    <AlertDialog
      open
      action="pair"
      title={t("notice.refundRevoked.title")}
      description={description}
      primaryLabel={t("blocked.account.support")}
      secondaryLabel={t("common.confirm")}
      onPrimary={() => {
        void ack();
        navigate("/support");
      }}
      onSecondary={() => void ack()}
    />
  );
}
